import { Coins } from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  type TooltipItem,
} from "chart.js";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/lib/i18n/messages";
import { formatPoints } from "@/lib/utils/format";
import { aggregatePoints } from "@/lib/utils/aggregate";
import { useDashboardPeriod } from "@/context/dashboard-period-context";
import type { Locale } from "@/types/auth";
import type { Transaction } from "@/types/wallet";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface PointsChartProps {
  locale: Locale;
  transactions: Transaction[];
  loading: boolean;
}

export function PointsChart({ locale, transactions, loading }: PointsChartProps) {
  const { period } = useDashboardPeriod();

  // aggregatePoints only supports weekly/monthly
  const aggPeriod: "weekly" | "monthly" =
    period === "daily" ? "weekly" : "monthly";

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 space-y-4" aria-hidden="true">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-4 w-48" />
        <div className="flex items-end gap-2 h-48">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 skeleton rounded animate-pulse"
              style={{ height: `${50 + i * 10}%` }}
            />
          ))}
        </div>
      </div>
    );
  }

  const aggregated = aggregatePoints(transactions, aggPeriod);
  const totalCp = aggregated.reduce((s, p) => s + p.cp, 0);
  const totalGp = aggregated.reduce((s, p) => s + p.gp, 0);
  const totalSp = aggregated.reduce((s, p) => s + p.sp, 0);
  const grandTotal = totalCp + totalGp + totalSp;

  if (grandTotal === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="text-sm font-medium text-foreground-secondary mb-4">
          {t(locale, "pointsEarned")}
        </h3>
        <EmptyState icon={Coins} title={t(locale, "noData")} className="border-0 py-8" />
      </div>
    );
  }

  const chartData = {
    labels: aggregated.map((p) => p.label),
    datasets: [
      {
        label: t(locale, "communityPoints"),
        data: aggregated.map((p) => p.cp),
        backgroundColor: "#0467C4",
        stack: "points",
        borderRadius: 4,
      },
      {
        label: t(locale, "greenPoints"),
        data: aggregated.map((p) => p.gp),
        backgroundColor: "#ACE405",
        stack: "points",
        borderRadius: 0,
      },
      {
        label: t(locale, "smartPoints"),
        data: aggregated.map((p) => p.sp),
        backgroundColor: "#FFA500",
        stack: "points",
        borderRadius: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { boxWidth: 12, font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<"bar">) => {
            return ` ${ctx.dataset.label ?? ""}: ${formatPoints(String(ctx.parsed.y ?? 0), locale)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, stacked: true },
      y: { beginAtZero: true, stacked: true, ticks: { precision: 0 } },
    },
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs space-y-5 h-full flex flex-col justify-between">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-brand-blue/10 text-brand-blue">
              <Coins className="w-4 h-4" />
            </span>
            <h3 className="text-base md:text-lg font-bold text-foreground">
              {t(locale, "pointsEarned")}
            </h3>
          </div>
          <p className="text-xs text-foreground-secondary mt-1">
            {locale === "ar"
              ? "توزيع نقاط المجتمع والخضراء والذكية المكتسبة."
              : "Distribution of Community, Green, and Smart points generated."}
          </p>
        </div>

        {/* Total Points Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/60 border border-border self-start sm:self-auto text-xs font-semibold text-foreground">
          <span>{t(locale, "totalPoints")}:</span>
          <span className="text-primary font-bold">{formatPoints(String(grandTotal), locale)}</span>
        </div>
      </div>

      <div className="h-64 sm:h-72 w-full pt-1">
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
